import React, { memo, useCallback, useState, useEffect } from 'react';
import Modal from 'components/ModalHoi';
import Context from 'managers/Context';

import Select from 'containers/components/Fields/Select';
import SelectFuzzy from 'containers/components/Fields/SelectFuzzy';
import { FuzzyMap } from 'utils/fuzzy';

import { getLiteral } from 'utils/getLiteral';

const REQUIRED_FIELDS = ['account', 'relationType'];

const getSelectValue = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'object') return value.value ?? value.id;
    return value;
};

const RelationModal = memo(({ entityId, relation, isOpen, toggleModal, onRefresh }) => {
    const [isLoading, setLoading] = useState(false);
    const [values, setValues] = useState({
        account: null,
        relationType: null,
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!isOpen) return;

        if (relation) {
            setValues({
                id: relation.id,
                account: getSelectValue(relation.account),
                relationType: getSelectValue(relation.relationType),
            });
        } else {
            setValues({
                account: null,
                relationType: null,
            });
        }
        setErrors({});
    }, [isOpen, relation]);

    const checkErrors = useCallback((values) => {
        const errors = REQUIRED_FIELDS.reduce((obj, key) => {
            if (!getSelectValue(values[key])) {
                obj[key] = getLiteral('helptext_required_field');
            }
            return obj;
        }, {});

        if (Object.keys(errors).length) {
            setErrors(errors);
            return true;
        }
    }, []);

    const onConfirm = useCallback(() => {
        if (checkErrors(values)) return;

        const data = {
            table: 'EMPRESA',
            parent: entityId,
            child: getSelectValue(values.account),
            relationType: getSelectValue(values.relationType),
        };

        let pr;
        if (values.id) pr = Context.domainManager.updateRelation(values.id, data);
        else pr = Context.domainManager.createRelation(data);

        setLoading(true);
        pr.then(() => {
            onRefresh();
        })
            .catch((err) => {
                console.error(err);
            })
            .then(() => {
                toggleModal(false);
                setLoading(false);
            });
    }, [checkErrors, values, entityId, onRefresh, toggleModal]);

    const onCancel = useCallback(() => {
        toggleModal(false);
    }, [toggleModal]);

    const onChange = useCallback(
        (field, value) => {
            let newValues = { ...values, [field]: value };
            setValues(newValues);
            // only if we had some errors
            if (Object.keys(errors).length) checkErrors(newValues);
        },
        [values, errors, checkErrors],
    );

    const fetchCompaniesOptions = useCallback(
        ({ list, field, text, feature }) => {
            return new Promise((resolve, reject) => {
                Context.domainManager.autoCompleteSearch({
                    entity: list,
                    field,
                    search: text,
                    feature,
                    success: (data) => {
                        const transformedData = data.map((item) => ({
                            ...item,
                            value: item.Id,
                            label: item.MatchingInfo,
                        }));

                        const filteredData = transformedData.filter((company) => {
                            return company.value !== entityId;
                        });

                        resolve(filteredData);
                    },
                    error: (error) => {
                        reject(error);
                    },
                });
            });
        },
        [entityId],
    );

    return (
        <Modal
            title={getLiteral('action_add_relation')}
            isOpen={isOpen}
            onRequestClose={onCancel}
            onCancel={onCancel}
            onConfirm={onConfirm}
            cancelText={getLiteral('action_cancel')}
            confirmText={getLiteral('action_save')}
            isConfirmLoading={isLoading}
        >
            <SelectFuzzy
                label={getLiteral('label_account')}
                labelMode="horizontal"
                onChange={(value) => onChange('account', value)}
                value={values.account}
                error={errors.account}
                mandatory
                usePlainValue
                onFetchOptions={fetchCompaniesOptions}
                {...FuzzyMap.empresas}
            />
            <Select
                label={getLiteral('label_relation_type')}
                labelMode="horizontal"
                list="tblTiposEmpresasRelations"
                onChange={(value) => onChange('relationType', value)}
                value={values.relationType}
                mandatory
                usePlainValue
                error={errors.relationType}
            />
        </Modal>
    );
});

export default RelationModal;
